import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Button,
  Tag,
  Space,
  Switch,
  Popconfirm,
  Spin,
  message,
  Alert,
} from 'antd';
import {
  PlusCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import {
  getTeamRoutingRules,
  createTeamRoutingRule,
  updateTeamRoutingRule,
  deleteTeamRoutingRule,
  reorderTeamRoutingRules,
  getAllLabelsForTeamRoutingRules,
  searchAddresseesForTeamRoutingRules,
} from '../../../actions/teamRoutingRules';
import { getOrganizationTeams } from '../../../actions/teams';
import { getDocumentCategories } from '../../../actions/userProfile';
import { captureAnalytics } from '../../../actions/preload';
import AddTeamRoutingRuleModal from '../../modal/AddTeamRoutingRuleModal';

// The login payload caches the caller's usable fax lines as
// [number, ownerName, ownerId, ownerType, faxId] tuples.
const parseAssociatedFaxLines = () => {
  try {
    const raw = JSON.parse(localStorage.getItem('associated_fax_numbers') || '[]');
    return raw.map(([number, ownerName, ownerId, ownerType, id]) => ({
      id,
      number,
      ownerName,
      ownerId,
      ownerType,
    }));
  } catch {
    return [];
  }
};

class ManageTeamRoutingRules extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      editingRule: null,
      localRules: [],
    };
    this.faxLines = parseAssociatedFaxLines();
  }

  async componentDidMount() {
    const {
      getTeamRoutingRules: fetchRules,
      getOrganizationTeams: fetchTeams,
      getDocumentCategories: fetchCategories,
      getAllLabelsForTeamRoutingRules: fetchLabels,
    } = this.props;
    await Promise.all([
      fetchRules(),
      fetchTeams(),
      fetchCategories(),
      fetchLabels(),
    ]);
    const { rules } = this.props;
    this.setState({ localRules: rules });
  }

  componentDidUpdate(prevProps) {
    const { rules } = this.props;
    if (prevProps.rules !== rules) {
      this.setState({ localRules: rules });
    }
  }

  openModal = (rule = null) => {
    captureAnalytics('team_routing_rule_modal_opened', { is_edit: !!rule });
    this.setState({ showModal: true, editingRule: rule });
  };

  closeModal = () => {
    this.setState({ showModal: false, editingRule: null });
  };

  handleSaveRule = async (ruleData) => {
    const { editingRule } = this.state;
    const {
      createTeamRoutingRule: createRule,
      updateTeamRoutingRule: updateRule,
      getTeamRoutingRules: fetchRules,
    } = this.props;

    const isUpdate = !!editingRule;
    let result;
    if (isUpdate) {
      result = await updateRule(editingRule.id, ruleData);
    } else {
      result = await createRule(ruleData);
    }

    if (result?.error) {
      message.error(result.response?.data?.error || 'Failed to save routing rule');
    } else {
      message.success(isUpdate ? 'Rule updated successfully' : 'Rule created successfully');
      this.closeModal();
      await fetchRules();
    }
  };

  handleDeleteRule = async (ruleId) => {
    const {
      deleteTeamRoutingRule: deleteRule,
      getTeamRoutingRules: fetchRules,
    } = this.props;

    const result = await deleteRule(ruleId);

    if (result?.error) {
      message.error(result.response?.data?.error || 'Failed to delete routing rule');
    } else {
      captureAnalytics('team_routing_rule_deleted');
      message.success('Rule deleted successfully');
      await fetchRules();
    }
  };

  // Toggles ride through the regular update endpoint; conditions are resent
  // unchanged (id-only rows are no-ops server side).
  handleToggle = async (rule, attrs) => {
    const { updateTeamRoutingRule: updateRule, getTeamRoutingRules: fetchRules } = this.props;

    if (Object.prototype.hasOwnProperty.call(attrs, 'active')) {
      captureAnalytics('team_routing_rule_active_toggled', { active: !!attrs.active });
    }

    const ruleData = {
      name: rule.name,
      team_slug: rule.team_slug,
      active: rule.active,
      label_match_mode: rule.label_match_mode,
      ...attrs,
      team_routing_conditions_attributes: rule.conditions.map((c) => ({
        id: c.id,
        kind: c.kind,
        operator: c.operator,
        value: c.value,
      })),
    };

    const result = await updateRule(rule.id, ruleData);

    if (result?.error) {
      message.error(result.response?.data?.error || 'Failed to update rule');
    } else {
      await fetchRules();
    }
  };

  moveRule = async (fromIndex, toIndex) => {
    const { localRules } = this.state;
    const { reorderTeamRoutingRules: reorder, getTeamRoutingRules: fetchRules } = this.props;

    if (toIndex < 0 || toIndex >= localRules.length) return;

    captureAnalytics('team_routing_rules_reordered', { rule_count: localRules.length });

    const newRules = [...localRules];
    const [moved] = newRules.splice(fromIndex, 1);
    newRules.splice(toIndex, 0, moved);

    this.setState({ localRules: newRules });

    const ruleIds = newRules.map((r) => r.id);
    const result = await reorder(ruleIds);

    if (result?.error) {
      message.error('Failed to reorder rules');
      await fetchRules();
    }
  };

  handleSearchAddressees = (search) => {
    const { searchAddresseesForTeamRoutingRules: searchAddressees } = this.props;
    if (!search || search.length < 2) return;
    searchAddressees(search);
  };

  checkFallbackWarning = () => {
    const { localRules } = this.state;
    const warnings = [];

    localRules.forEach((rule, index) => {
      const isCatchAll = !rule.conditions || rule.conditions.length === 0;
      if (isCatchAll && index < localRules.length - 1) {
        warnings.push(
          `"${rule.name || 'Fallback rule'}" (priority ${index + 1}) has no conditions and will catch every document before the rules below it.`,
        );
      }
    });

    return warnings;
  };

  renderConditions = (rule) => {
    const pieces = [];

    if (rule.document_category) {
      pieces.push(<Tag key="category">{rule.document_category.emr_category}</Tag>);
    }

    const ruleLabels = rule.labels || [];
    if (ruleLabels.length > 0) {
      const connector = rule.label_match_mode === 'all' ? 'and' : 'or';
      ruleLabels.forEach((ruleLabel, idx) => {
        const textColor = ruleLabel.is_dark_text ? 'black' : 'white';
        pieces.push(
          <React.Fragment key={`label-${ruleLabel.id}`}>
            <Tag color={ruleLabel.color} style={{ color: textColor, marginRight: 0 }}>
              {ruleLabel.name}
            </Tag>
            {idx < ruleLabels.length - 1 && (
              <span style={{ color: '#888', fontSize: '12px', margin: '0 4px' }}>{connector}</span>
            )}
          </React.Fragment>,
        );
      });
    }

    if (rule.fax_line) {
      pieces.push(<Tag key="fax_line">{`Line: ${rule.fax_line.number}`}</Tag>);
    }
    if (rule.sender) {
      pieces.push(<Tag key="sender">{`From: ${rule.sender}`}</Tag>);
    }
    if (rule.addressee) {
      pieces.push(
        <Tag key="addressee">
          {`Sender Contact: ${rule.addressee.contact_name || rule.addressee.contact}`}
        </Tag>,
      );
    }
    if (pieces.length === 0) {
      return <span style={{ color: '#888', fontStyle: 'italic' }}>(No conditions - catches all)</span>;
    }

    return <Space size={4} wrap>{pieces}</Space>;
  };

  render() {
    const { showModal, editingRule, localRules } = this.state;
    const {
      isLoading,
      isAdmin,
      userInfoLoaded,
      teams,
      categories,
      labels,
      addressees,
    } = this.props;

    // Allow editing if user is admin OR if user info hasn't loaded yet
    // (backend will reject if not admin, this just improves UX during load)
    const canEdit = isAdmin || !userInfoLoaded;

    const warnings = this.checkFallbackWarning();

    const columns = [
      {
        title: 'Priority',
        key: 'priority',
        width: 100,
        align: 'center',
        render: (_text, _record, index) => (
          <Space size={4}>
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={() => this.moveRule(index, index - 1)}
              disabled={index === 0 || !canEdit}
              size="small"
            />
            <Button
              type="text"
              icon={<ArrowDownOutlined />}
              onClick={() => this.moveRule(index, index + 1)}
              disabled={index === localRules.length - 1 || !canEdit}
              size="small"
            />
            <span style={{ width: 24, display: 'inline-block', textAlign: 'right' }}>
              {index + 1}
            </span>
          </Space>
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name) => name || '-',
      },
      {
        title: 'Conditions',
        key: 'conditions',
        render: (_, record) => this.renderConditions(record),
      },
      {
        title: 'Team',
        dataIndex: 'team',
        key: 'team',
        render: (team) => team?.name || '-',
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        width: 80,
        align: 'center',
        render: (active, record) => (
          <Switch
            checked={active}
            onChange={(checked) => this.handleToggle(record, { active: checked })}
            disabled={!canEdit}
            size="small"
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 150,
        align: 'right',
        render: (_, record) => (
          <Space>
            <Button
              size="small"
              onClick={() => this.openModal(record)}
              disabled={!canEdit}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              onConfirm={() => this.handleDeleteRule(record.id)}
              disabled={!canEdit}
            >
              <Button size="small" danger disabled={!canEdit}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    if (isLoading && localRules.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex', flexDirection: 'column', width: '100%', padding: '10px 40px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ margin: 0, marginRight: 16 }}>Team Routing Rules</h2>
            {canEdit && (
              <Button
                icon={<PlusCircleOutlined />}
                type="primary"
                onClick={() => this.openModal()}
              >
                Add Rule
              </Button>
            )}
          </div>
        </div>

        <div className="team-guidance">
          <div className="team-guidance__header">
            <div>
              <div className="team-guidance__eyebrow">Teams and routing</div>
              <h3 className="team-guidance__title">How team routing rules work</h3>
              <p className="team-guidance__lede">
                Routing rules automatically tag inbound documents with a team after they are
                labeled and categorized.
              </p>
            </div>
            <div className="team-guidance__summary">
              Rules are evaluated from top to bottom, and the first rule that matches wins.
            </div>
          </div>

          <div className="team-guidance__columns">
            <div className="team-guidance__section">
              <h4>How it works</h4>
              <ol className="team-guidance__steps">
                <li>All of a rule&apos;s conditions must match for the rule to apply.</li>
                <li>Rules are checked top to bottom — the first match tags the document with that team.</li>
                <li>A rule with no conditions is a catch-all that matches everything, so place it last.</li>
              </ol>
            </div>
            <div className="team-guidance__section">
              <h4>Important to know</h4>
              <ul className="team-guidance__list">
                <li>A rule never overwrites a document that already has a team.</li>
                <li>The team&apos;s members are notified per their notification settings when a document is routed.</li>
              </ul>
            </div>
          </div>
        </div>

        {warnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Ordering Warning"
            description={(
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          size="small"
          dataSource={localRules}
          columns={columns}
          rowKey={(record) => record.id}
          bordered
          pagination={false}
          loading={isLoading}
        />

        {showModal && (
          <AddTeamRoutingRuleModal
            visible={showModal}
            onClose={this.closeModal}
            onSave={this.handleSaveRule}
            editingRule={editingRule}
            teams={teams}
            categories={categories}
            labels={labels}
            faxLines={this.faxLines}
            addresseeOptions={addressees}
            onSearchAddressees={this.handleSearchAddressees}
            isLoading={isLoading}
          />
        )}
      </div>
    );
  }
}

ManageTeamRoutingRules.defaultProps = {
  rules: [],
  teams: [],
  categories: [],
  labels: [],
  addressees: [],
  isLoading: false,
  userInfoLoaded: false,
};

ManageTeamRoutingRules.propTypes = {
  rules: PropTypes.array,
  teams: PropTypes.array,
  categories: PropTypes.array,
  labels: PropTypes.array,
  addressees: PropTypes.array,
  isLoading: PropTypes.bool,
  isAdmin: PropTypes.bool.isRequired,
  userInfoLoaded: PropTypes.bool,
  getTeamRoutingRules: PropTypes.func.isRequired,
  createTeamRoutingRule: PropTypes.func.isRequired,
  updateTeamRoutingRule: PropTypes.func.isRequired,
  deleteTeamRoutingRule: PropTypes.func.isRequired,
  reorderTeamRoutingRules: PropTypes.func.isRequired,
  getOrganizationTeams: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  getAllLabelsForTeamRoutingRules: PropTypes.func.isRequired,
  searchAddresseesForTeamRoutingRules: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    rules: state.teamRoutingRules.rules,
    isLoading: state.teamRoutingRules.isLoading,
    labels: state.teamRoutingRules.labels || [],
    addressees: state.teamRoutingRules.addressees || [],
    teams: state.teams.data || [],
    isAdmin: state.userProfile.isAdmin,
    userInfoLoaded: !!state.userProfile.email,
    categories: state.userProfile.allDocumentCategories?.categories || [],
  }),
  {
    getTeamRoutingRules,
    createTeamRoutingRule,
    updateTeamRoutingRule,
    deleteTeamRoutingRule,
    reorderTeamRoutingRules,
    getOrganizationTeams,
    getDocumentCategories,
    getAllLabelsForTeamRoutingRules,
    searchAddresseesForTeamRoutingRules,
  },
)(ManageTeamRoutingRules);
